import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import ItemSearchSelect from "@/components/ItemSearchSelect";
import CustomerPicker, { ensureCustomer } from "@/components/CustomerPicker";
import { Plus, Trash, PencilSimple, Printer, ArrowRight, X, WhatsappLogo } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-700",
  accepted: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  expired: "bg-orange-100 text-orange-700",
  converted: "bg-purple-100 text-purple-700",
};

export default function Quotations() {
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const nav = useNavigate();

  const [customerId, setCustomerId] = useState("");
  const [newCust, setNewCust] = useState(null);
  const [quoteDate, setQuoteDate] = useState(new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState("");
  const [quoteNo, setQuoteNo] = useState("");
  const [status, setStatus] = useState("draft");
  const [remarks, setRemarks] = useState("");
  const [discountType, setDiscountType] = useState("NONE");
  const [discountValue, setDiscountValue] = useState(0);
  const [lines, setLines] = useState([{ item_id: "", qty: 1, price: 0 }]);
  const [shipToOpen, setShipToOpen] = useState(false);
  const [shipTo, setShipTo] = useState({ name: "", address: "", city: "", state: "", mobile: "", gst: "" });

  const load = async () => {
    try {
      const [q, c] = await Promise.all([api.get("/quotes"), api.get("/customers")]);
      setRows(q.data); setParties(c.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditId(null); setCustomerId(""); setNewCust(null); setQuoteDate(new Date().toISOString().slice(0, 10));
    setValidUntil(""); setQuoteNo(""); setStatus("draft"); setRemarks("");
    setDiscountType("NONE"); setDiscountValue(0);
    setLines([{ item_id: "", qty: 1, price: 0 }]);
    setShipTo({ name: "", address: "", city: "", state: "", mobile: "", gst: "" });
    setShipToOpen(false);
  };

  const openNew = async () => {
    resetForm();
    try {
      const { data } = await api.get("/quotes/next-quote-no");
      setQuoteNo(data.quote_no);
    } catch {}
    setShowForm(true);
  };

  const openEdit = async (r) => {
    try {
      const { data } = await api.get(`/quotes/${r.id}`);
      setEditId(data.id);
      setNewCust(null);
      setCustomerId(data.customer_id || "");
      setQuoteDate(data.quote_date || new Date().toISOString().slice(0, 10));
      setValidUntil(data.valid_until || "");
      setQuoteNo(data.quote_no || "");
      setStatus(data.status || "draft");
      setRemarks(data.remarks || "");
      setDiscountType(data.discount_type || "NONE");
      setDiscountValue(data.discount_value || 0);
      setLines((data.items || []).map(li => ({
        item_id: li.item_id, qty: li.qty, price: li.price,
        item: { id: li.item_id, item_code: li.item_code, item_name: li.item_name, brand: li.brand, gst_rate: li.gst_rate, item_type: li.item_type || "GOODS", sale_price: li.price },
      })));
      setShipTo({
        name: data.ship_to_name || "", address: data.ship_to_address || "",
        city: data.ship_to_city || "", state: data.ship_to_state || "",
        mobile: data.ship_to_mobile || "", gst: data.ship_to_gst || "",
      });
      setShipToOpen(!!(data.ship_to_name || data.ship_to_address));
      setShowForm(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete quote ${r.quote_no}?`)) return;
    try { await api.delete(`/quotes/${r.id}`); toast.success("Quote deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const convertToSale = async (r) => {
    if (!window.confirm(`Convert quote ${r.quote_no} to a sale invoice? Stock will be deducted.`)) return;
    try {
      const { data } = await api.post(`/quotes/${r.id}/convert`);
      toast.success(`Converted to invoice ${data.invoice_no}`);
      load();
      nav(`/invoice/${data.sale_id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const shareWhatsApp = async (r) => {
    try {
      const { data } = await api.get(`/quotes/${r.id}`);
      const cust = data.customer || {};
      const mobile = (cust.mobile || "").replace(/\D/g, "");
      if (!mobile) {
        toast.error("Customer ka mobile number nahi hai. Pehle customer master me add karein.");
        return;
      }
      const num = mobile.length === 10 ? `91${mobile}` : mobile;
      const name = cust.customer_name || cust.first_name || "Customer";
      const shareUrl = `${window.location.origin}/public/quote/${r.id}`;
      const total = money(data.total_amount || 0);
      const msg =
        `Namaste ${name},\n\n` +
        `Aapka Quotation *${data.quote_no}* — Total: *${total}*\n\n` +
        `View / Download: ${shareUrl}\n\n` +
        `Thank you,\nShree Ram Enterprises`;
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const updLine = (i, key, val, itemObj = null) => {
    const nl = [...lines];
    nl[i] = { ...nl[i], [key]: val };
    if (key === "item_id" && itemObj) { nl[i].item = itemObj; nl[i].price = itemObj.sale_price ?? 0; }
    setLines(nl);
  };
  const addLine = () => setLines([...lines, { item_id: "", qty: 1, price: 0 }]);
  const delLine = (i) => setLines(lines.filter((_, x) => x !== i));

  // Totals
  const totals = (() => {
    const raw = lines.reduce((a, l) => {
      const gst = l.item ? Number(l.item.gst_rate || 0) : 0;
      const amt = Number(l.qty || 0) * Number(l.price || 0);
      const goods = (l.item?.item_type || "GOODS").toUpperCase() !== "SERVICE";
      a.sub += amt; if (goods) a.goods += amt;
      a.gst.push({ amt, gst, goods });
      return a;
    }, { sub: 0, goods: 0, gst: [] });
    let disc = 0;
    const dv = Number(discountValue || 0);
    // Discount only on parts (GOODS), never on service charges
    if (discountType === "PERCENT") disc = raw.goods * dv / 100;
    else if (discountType === "FLAT") disc = Math.min(dv, raw.goods);
    disc = Math.max(0, disc);
    const scale = raw.goods > 0 ? (raw.goods - disc) / raw.goods : 1;
    const tax = raw.gst.reduce((s, x) => s + x.amt * (x.goods ? scale : 1) * x.gst / 100, 0);
    const preRound = raw.sub - disc + tax;
    const total = Math.round(preRound);
    return { sub: raw.sub, discount: disc, taxable: raw.sub - disc, tax, roundOff: total - preRound, total, scale };
  })();

  const save = async () => {
    if (newCust && !newCust.customer_name.trim()) return toast.error("New customer ka naam daalein");
    if (!newCust && !customerId) return toast.error("Select customer");
    if (lines.some(l => !l.item_id || !l.qty)) return toast.error("Fill all line items");
    setBusy(true);
    try {
      let cid = customerId;
      if (newCust) {
        cid = await ensureCustomer(newCust, parties);
        setNewCust(null); setCustomerId(cid);
        api.get("/customers").then(r => setParties(r.data)).catch(() => {});
      }
      const payload = {
        customer_id: cid,
        quote_date: quoteDate,
        valid_until: validUntil,
        quote_no: quoteNo,
        status,
        items: lines.map(l => ({ item_id: l.item_id, qty: parseFloat(l.qty), price: parseFloat(l.price) })),
        remarks,
        discount_type: discountType,
        discount_value: parseFloat(discountValue) || 0,
        ship_to_name: shipTo.name, ship_to_address: shipTo.address,
        ship_to_city: shipTo.city, ship_to_state: shipTo.state,
        ship_to_mobile: shipTo.mobile, ship_to_gst: shipTo.gst,
      };
      if (editId) await api.put(`/quotes/${editId}`, payload);
      else await api.post("/quotes", payload);
      toast.success(editId ? "Quote updated" : "Quote created");
      setShowForm(false); resetForm(); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const onCustomerChange = (id) => {
    setCustomerId(id);
    if (!id) return;
    const c = parties.find(p => p.id === id);
    if (c && !editId) {
      setShipTo({
        name: c.shipping_attention || c.customer_name || "",
        address: [c.shipping_street1, c.shipping_street2].filter(Boolean).join(", "),
        city: c.shipping_city || "", state: c.shipping_state || "",
        mobile: c.mobile || "", gst: c.gst_no || "",
      });
    }
  };

  const s = search.trim().toLowerCase();
  const filtered = !s ? rows : rows.filter(r =>
    (r.quote_no || "").toLowerCase().includes(s) ||
    (r.customer_name || "").toLowerCase().includes(s) ||
    (r.status || "").toLowerCase().includes(s) ||
    (r.quote_date || "").toLowerCase().includes(s)
  );

  return (
    <div className="space-y-4" data-testid="quotations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Quotations</h1>
          <p className="text-sm text-muted-foreground mt-1">Non-binding estimates. Convert accepted quotes into invoices with one click.</p>
        </div>
        <Button onClick={openNew} data-testid="quote-toggle" className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
          <Plus size={16} className="mr-1" weight="bold" /> New Quotation
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">{editId ? `Edit Quote — ${quoteNo}` : `New Quotation`}</h3>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="text-muted-foreground hover:text-destructive"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className={newCust ? "md:col-span-4" : ""}>
              <Label>Customer*</Label>
              <CustomerPicker value={customerId} onChange={onCustomerChange} testid="quote-customer"
                parties={parties} newCust={newCust} setNewCust={setNewCust} disableNew={!!editId} />
            </div>
            <div><Label>Quote No</Label><Input value={quoteNo} onChange={e => setQuoteNo(e.target.value)} data-testid="quote-no" /></div>
            <div><Label>Quote Date</Label><Input type="date" value={quoteDate} onChange={e => setQuoteDate(e.target.value)} data-testid="quote-date" /></div>
            <div><Label>Valid Until</Label><Input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} data-testid="quote-valid" /></div>
            <div>
              <Label>Status</Label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid="quote-status">
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          <div className="mb-4 border border-border rounded">
            <button type="button" onClick={() => setShipToOpen(!shipToOpen)}
              className="w-full flex items-center justify-between px-4 py-2 text-sm font-semibold bg-amber-50/60 hover:bg-amber-50 rounded-t" data-testid="quote-shipto-toggle">
              <span>📦 Ship To — {shipToOpen ? "hide" : (shipTo.address ? "edit" : "add if different from Bill To")}</span>
              <span className="text-xs text-muted-foreground">{shipToOpen ? "▲" : "▼"}</span>
            </button>
            {shipToOpen && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label>Consignee Name</Label><Input value={shipTo.name} onChange={e => setShipTo({ ...shipTo, name: e.target.value })} /></div>
                <div><Label>Mobile</Label><Input value={shipTo.mobile} onChange={e => setShipTo({ ...shipTo, mobile: e.target.value })} /></div>
                <div><Label>GSTIN</Label><Input value={shipTo.gst} onChange={e => setShipTo({ ...shipTo, gst: e.target.value })} /></div>
                <div className="md:col-span-3"><Label>Address</Label><Input value={shipTo.address} onChange={e => setShipTo({ ...shipTo, address: e.target.value })} /></div>
                <div><Label>City</Label><Input value={shipTo.city} onChange={e => setShipTo({ ...shipTo, city: e.target.value })} /></div>
                <div><Label>State</Label><Input value={shipTo.state} onChange={e => setShipTo({ ...shipTo, state: e.target.value })} /></div>
              </div>
            )}
          </div>

          <div className="border border-border rounded mb-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="w-24">Qty</TableHead>
                  <TableHead className="w-32">Rate (₹)</TableHead>
                  <TableHead className="w-20">GST%</TableHead>
                  <TableHead className="w-32 text-right">Line Total</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l, i) => {
                  const gst = l.item ? Number(l.item.gst_rate || 0) : 0;
                  const amt = Number(l.qty || 0) * Number(l.price || 0) * ((l.item?.item_type || "GOODS").toUpperCase() !== "SERVICE" ? totals.scale : 1);
                  const total = amt + amt * gst / 100;
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <ItemSearchSelect value={l.item_id} onChange={(id, it) => updLine(i, "item_id", id, it)}
                          items={l.item ? [l.item] : []} testid={`quote-line-item-${i}`} />
                      </TableCell>
                      <TableCell><Input type="number" value={l.qty} onChange={e => updLine(i, "qty", e.target.value)} /></TableCell>
                      <TableCell><Input type="number" value={l.price} onChange={e => updLine(i, "price", e.target.value)} /></TableCell>
                      <TableCell>{gst}%</TableCell>
                      <TableCell className="text-right tabular-nums">{money(total)}</TableCell>
                      <TableCell><button onClick={() => delLine(i)} className="p-1 text-destructive hover:bg-destructive/10 rounded"><Trash size={13} /></button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="p-2 border-t border-border">
              <Button variant="outline" size="sm" onClick={addLine} data-testid="quote-add-line"><Plus size={14} className="mr-1" /> Add Line</Button>
            </div>
          </div>

          <div className="flex justify-between items-end">
            <div className="w-96 space-y-3">
              <div><Label>Remarks</Label><Input value={remarks} onChange={e => setRemarks(e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label title="Discount sirf parts par lagta hai, service charge par nahi">Discount Type <span className="text-[10px] text-muted-foreground font-normal">(parts only)</span></Label>
                  <select value={discountType} onChange={e => setDiscountType(e.target.value)} className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid="quote-discount-type">
                    <option value="NONE">No discount</option>
                    <option value="FLAT">Flat ₹ off</option>
                    <option value="PERCENT">% off</option>
                  </select>
                </div>
                <div>
                  <Label>{discountType === "PERCENT" ? "%" : "₹"}</Label>
                  <Input type="number" value={discountValue} onChange={e => setDiscountValue(e.target.value)} disabled={discountType === "NONE"} data-testid="quote-discount-value" />
                </div>
              </div>
            </div>
            <div className="text-right space-y-1 min-w-[240px]">
              <div className="text-sm"><span className="text-muted-foreground mr-4">Subtotal:</span>{money(totals.sub)}</div>
              {totals.discount > 0 && (
                <>
                  <div className="text-sm text-orange-700"><span className="text-muted-foreground mr-4">Discount:</span>-{money(totals.discount)}</div>
                  <div className="text-sm"><span className="text-muted-foreground mr-4">Taxable:</span>{money(totals.taxable)}</div>
                </>
              )}
              <div className="text-sm"><span className="text-muted-foreground mr-4">Tax:</span>{money(totals.tax)}</div>
              {Math.abs(totals.roundOff) > 0.001 && (
                <div className="text-sm" data-testid="quote-round-off"><span className="text-muted-foreground mr-4">Round Off:</span>{totals.roundOff > 0 ? "+" : "-"}{money(Math.abs(totals.roundOff))}</div>
              )}
              <div className="font-display text-2xl font-bold"><span className="text-muted-foreground text-sm mr-4">Total:</span>{money(totals.total)}</div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="quote-save" className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
              {busy ? "Saving..." : (editId ? "Update Quote" : "Save Quote")}
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-0 border border-border overflow-hidden">
        <div className="p-3 border-b border-border flex items-center gap-3 bg-secondary/30">
          <Input placeholder="🔍 Search quote #, customer, status, date…" value={search} onChange={e => setSearch(e.target.value)} className="flex-1 h-9" data-testid="quote-search" />
          <span className="text-xs text-muted-foreground tabular-nums">{filtered.length} of {rows.length}</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quote #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Valid Until</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right w-40">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(r => (
              <TableRow key={r.id} data-testid={`quote-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.quote_no}</TableCell>
                <TableCell className="text-xs">{r.quote_date}</TableCell>
                <TableCell>{r.customer_name}</TableCell>
                <TableCell className="text-xs">{r.valid_until || "-"}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${STATUS_BADGE[r.status] || STATUS_BADGE.draft}`}>
                    {r.status}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{money(r.total_amount)}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => nav(`/quote/${r.id}`)} data-testid={`quote-print-${r.id}`} className="p-1.5 rounded hover:bg-secondary mr-1" title="Print / PDF"><Printer size={16} /></button>
                  <button onClick={() => shareWhatsApp(r)} data-testid={`quote-whatsapp-${r.id}`} className="p-1.5 rounded hover:bg-secondary text-[#25D366] mr-1" title="Share on WhatsApp"><WhatsappLogo size={16} weight="fill" /></button>
                  {r.status !== "converted" && (
                    <>
                      <button onClick={() => openEdit(r)} data-testid={`quote-edit-${r.id}`} className="p-1.5 rounded hover:bg-secondary text-blue-600 mr-1" title="Edit"><PencilSimple size={16} /></button>
                      <button onClick={() => convertToSale(r)} data-testid={`quote-convert-${r.id}`} className="p-1.5 rounded hover:bg-secondary text-green-700 mr-1" title="Convert to Invoice"><ArrowRight size={16} weight="bold" /></button>
                    </>
                  )}
                  <button onClick={() => remove(r)} data-testid={`quote-delete-${r.id}`} className="p-1.5 rounded hover:bg-destructive/10 text-destructive" title="Delete"><Trash size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No quotations yet. Click "New Quotation" to create one.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
